import { useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import { useState } from 'react';
import { updateSEO } from '../../utils/seo';

// 紺帯番号バンド(thebutler型 章帯 B-3 の由来注記付き移植)。
// 由来: site-thebutler の ChapterBand(src/pages/home/page.tsx L76-118)+実スクショ tb-3/tb-4。
// 文法: 紋章(左)+ローマ番号+和文章名(左詰め)+英字イタリック(右詰め)。帯=区切り(この帯のある章境界には
// 別途の区切りラインを入れない=チェックリスト#31)。帯直後に同一文言の見出しを反復しない(#33)。
function NavyBand({ no, ja, en }: { no: string; ja: string; en: string }) {
  return (
    <div className="bg-[#1a2942] border-y border-[#a38e65]/25">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-[26px] flex items-center gap-4 sm:gap-6">
        <img
          src="/images/brand/jba-logo.png"
          alt=""
          aria-hidden="true"
          className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0 opacity-90"
        />
        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl sm:text-[26px] tracking-[0.1em] shrink-0 leading-none">{no}</span>
        <h2 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-2xl lg:text-[26px] tracking-[0.08em] leading-tight">{ja}</h2>
        <span className="hidden md:block ml-auto font-['Playfair_Display',serif] italic text-[#a38e65] text-base lg:text-[19px] tracking-[0.12em]">{en}</span>
      </div>
    </div>
  );
}

// 料金数値の共通表示。主数値を大きく強調し、単位「円」(および末尾の「〜」)は小さめに添える。
// value を「円」位置で分割して連結レンダリングするだけで、文字列自体は一切改変しない(main+unit===value)。
function Fee({ value, className, unitClassName }: { value: string; className: string; unitClassName: string }) {
  const i = value.indexOf('円');
  const main = i >= 0 ? value.slice(0, i) : value;
  const unit = i >= 0 ? value.slice(i) : '';
  return (
    <span className={className}>{main}{unit && <span className={unitClassName}>{unit}</span>}</span>
  );
}

export default function Home() {
  const [showMoreQualifications, setShowMoreQualifications] = useState(false);

  useEffect(() => {
    updateSEO({
      title: '一般社団法人 全日本執事協会 | オフィシャルサイト',
      description: '一般社団法人 全日本執事協会は、執事文化の普及と専門知識の向上を目指す日本唯一の執事専門団体です。初級から上級までの執事資格認定制度、食事サービスやワイン、ビジネスマナーなど6分野の専門資格、実践的なセミナー・講座の開催、国際交流プログラムを通じて、世界に通用するプロフェッショナルな執事を育成しています。執事を目指す方、ホスピタリティスキルを高めたい方、法人研修をお考えの企業様はぜひご相談ください。',
      keywords: '執事協会,執事資格,執事サービス,バトラー,ホスピタリティ,おもてなし,執事養成,執事派遣,執事研修,全日本執事協会',
      canonical: 'https://butler-association.jp'
    });
  }, []);

  const toggleMoreQualifications = () => {
    setShowMoreQualifications(!showMoreQualifications);
  };

  // BTN-08 共通クラス(2態・二重管理回避)。明背景=紺塗り+真鍮枠+生成り文字→hover真鍮塗り+紺枠 / 紺背景=塗りと枠を反転(真鍮塗り+紺枠+紺文字)→hover紺塗り+真鍮枠+生成り文字
  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[46px] py-[18px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[46px] py-[18px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  // テキスト矢印CTA(thebutler型: 下線付き・記号矢印・塗りなし)。tb-2「この利用を相談する →」由来。
  const TEXT_ARROW = "mt-auto self-start inline-flex items-center gap-2 font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942] tracking-[0.06em] border-b border-[#a38e65] pb-1 hover:text-[#8f7a56] transition-colors duration-300";

  return (
    <>
      <Layout>
      {/* ヒーローエリア（担当外・不変） */}
      <section className="home-hero relative min-h-[600px] overflow-hidden bg-[#1a2942] h-screen lg:h-auto lg:pt-[315px]">
        {/* ビルボード枠: 固定看板ヘッダー分を lg:pt で空け重なりゼロ。枠は画像アスペクト(1376/752)に一致=隙間/過剰クロップ/頭切れゼロ */}
        <div className="relative h-full lg:h-auto lg:aspect-[1376/752] overflow-hidden">
          <div
            role="img"
            aria-label="執事サロンの応接室に立つシニアバトラー。シャンデリアとアンティーク家具、蓄音機のある実在のサロンで、黒モーニングコートに白手袋の正装で来訪者を迎える全日本執事協会の執事"
            className="absolute inset-0 bg-cover bg-[center_20%] lg:bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('/images/generated/jba-p-000-home-hero.jpg')`
            }}
          >
            <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(10,12,14,0.78)_0%,rgba(10,12,14,0.35)_55%,rgba(10,12,14,0.15)_100%)]"></div>
          </div>
          <div className="absolute inset-0 flex items-center">
            <div className="relative z-10 text-left text-white w-full max-w-[620px] px-6 sm:px-10 lg:px-16"><span className="block font-['Playfair_Display',serif] uppercase tracking-[0.34em] text-xs sm:text-[13px] text-[#d7b779] drop-shadow-lg">Japan Butler Association</span>
          <div className="w-14 h-px bg-[#a38e65] my-5"></div>
          <h1 className="text-[clamp(26px,3.4vw,44px)] jba-display font-normal mb-6 leading-[1.35] tracking-[0.04em] text-white text-left drop-shadow-2xl">
            <span className="block">洗練された執事文化を</span><span className="block">日本に広めるために</span>
          </h1>
          <p className="text-base sm:text-lg lg:text-xl leading-[1.85] opacity-95 font-['Noto_Serif_JP',serif] text-left drop-shadow-lg">
            <span className="inline-block">一般社団法人 全日本執事協会は、</span><span className="inline-block">執事文化の普及と</span><span className="inline-block">専門知識の向上を目指し、</span><span className="inline-block">資格制度の確立や</span><span className="inline-block">セミナー開催など</span><span className="inline-block">様々な活動を行っています。</span>
          </p>
          <div className="mt-7">
            <a
              href="/about/overview"
              className={BTN08_ON_LIGHT}
            >
              協会について詳しく
            </a>
          </div></div>
          </div>
          <div className="absolute bottom-8 sm:bottom-12 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
            <i className="ri-arrow-down-line text-2xl sm:text-3xl drop-shadow-lg"></i>
          </div>
        </div>
      </section>

      {/* ===== I. 協会について — ABOUT-F 帯反転（カタログ docs/design-catalog/sec-about-variants.html .ab-f。既採用・維持）===== */}
      <section className="pt-16 sm:pt-20 lg:pt-24 pb-10 sm:pb-14 bg-white relative overflow-hidden">
        <div className="px-4 sm:px-6 lg:px-10">
          <div className="bg-[#1a2942] grid lg:grid-cols-[0.52fr_0.48fr] overflow-hidden">
            {/* 左: テキスト列（金eyebrow→生成り見出し→真鍮短罫→本文→左罫引用→CTA） */}
            <div className="order-2 lg:order-1 px-8 sm:px-12 lg:px-14 py-14 sm:py-16 lg:py-[70px] flex flex-col justify-center text-left">
              <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.3em] text-xs text-[#d7b779] mb-3">About the Association</span>
              <h2 className="text-2xl sm:text-3xl lg:text-[31px] jba-display font-medium text-[#f8f6f0] tracking-[0.04em] leading-[1.42] mb-0">
                I. 協会について
              </h2>
              <div className="w-11 h-px bg-[#a38e65] mt-[14px] mb-[22px]"></div>
              <div className="space-y-[18px] text-base text-[#e7e2d8] leading-[1.95] font-['Noto_Serif_JP',serif]">
                <p>
                  全日本執事協会は、<span className="text-[22px] font-bold text-[#d7b779] font-['Playfair_Display',serif] block my-2">1890年</span>（明治23年）の創設以来、日本における執事文化の普及と発展を目的として活動を続けてきました。
                </p>
                <p>
                  ヨーロッパを起源とする執事の伝統を日本の文化に融合させながら、現代のライフスタイルに合わせた新しい執事像の確立を目指しています。
                </p>
                <p>
                  資格認定制度の確立、専門セミナーの開催、国際交流プログラムの運営を通じて、世界に通用するプロフェッショナルな執事を育成しています。
                </p>
              </div>
              <blockquote className="border-l-2 border-[#a38e65] pl-4 my-6 text-base sm:text-lg text-[#e7d9b6] font-['Noto_Serif_JP',serif] leading-[1.8]">
                「おもてなしの心」と「プロフェッショナルなサービス」の融合
              </blockquote>
              <div className="mt-2">
                <a href="/about/overview" className={BTN08_ON_NAVY}>
                  詳しく見る
                </a>
              </div>
            </div>
            {/* 右: 画像列フル */}
            <div className="order-1 lg:order-2 relative min-h-[320px] sm:min-h-[420px] lg:min-h-[460px] bg-black">
              <img
                src="/images/generated/jba-still-tableware-01.jpg"
                alt="金彩と黒の洋食器"
                className="absolute inset-0 w-full h-full object-cover opacity-90"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== II. 執事を利用する — thebutler型 白カード（画像上／英字イタリックeyebrow／見出し／本文5行超／金◆下線箇条書き／細罫データ行／テキスト矢印CTA）===== */}
      <NavyBand no="II" ja="執事を利用する" en="Butler Services" />
      <section className="py-12 sm:py-16 bg-white relative overflow-hidden">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          {/* 金差し色フレーズ見出し + リード3行超（帯タイトルは反復しない #33） */}
          <div className="max-w-4xl mb-10 sm:mb-14 text-left">
            <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-5">
              必要な分だけ、選べる<span className="text-[#a38e65]">執事サービス</span>
            </h3>
            <p className="text-base sm:text-lg text-[#333] leading-[1.95] font-['Noto_Serif_JP',serif]">
              全日本執事協会が運営する執事人材サービス「THE BUTLER」では、短時間のアシスタンスから24時間体制の完全サポートまで、お客様のニーズに合わせた執事サービスを提供しています。経験豊富な執事が、日常生活からビジネスシーン、VIP対応、特別なイベントまで幅広くサポートいたします。一度きりのご利用から、暮らしのそばに執事のいる時間をお確かめいただけます。
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
            {/* Footman — 白カード */}
            <article className="bg-white border border-[#e2ddd2] flex flex-col group hover:border-[#a38e65]/70 transition-colors duration-300">
              <div className="relative h-56 sm:h-60 md:h-64 overflow-hidden">
                <img
                  src="/images/generated/jba-p-001-home-footman.jpg"
                  alt="フットマンサービスを担う執事が客邸の玄関で来客の上着を預かり、日常の家事や秘書業務を支える基本的な接遇の様子"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-7 sm:p-8 md:p-9 flex flex-col flex-1 text-left">
                <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm tracking-[0.06em] mb-3">i. Footman</span>
                <h4 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl md:text-[24px] leading-[1.5] tracking-[0.02em] mb-4">基本的な執事サービス</h4>
                <p className="font-['Noto_Serif_JP',serif] text-[#333] text-sm sm:text-base leading-[1.95] mb-6">
                  一定期間や特定のイベントにおいて、プロの執事を利用できる基礎的なサービスです。家事全般をはじめとする日常生活のサポートやゲスト対応を行い、お客様に快適な生活を提供します。来客対応や簡単な秘書業務にも対応し、パーティーやイベントでの一時的なアシスタントとしても活躍します。
                </p>
                <ul className="space-y-0 mb-7">
                  <li className="flex items-start gap-3 border-b border-[#e2ddd2] py-2.5 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]"><span className="text-[#a38e65] text-[10px] mt-[7px]">◆</span><span>掃除・洗濯・料理など家事全般</span></li>
                  <li className="flex items-start gap-3 border-b border-[#e2ddd2] py-2.5 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]"><span className="text-[#a38e65] text-[10px] mt-[7px]">◆</span><span>来客対応・応接の補助</span></li>
                  <li className="flex items-start gap-3 border-b border-[#e2ddd2] py-2.5 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]"><span className="text-[#a38e65] text-[10px] mt-[7px]">◆</span><span>簡単な秘書業務・買い出し</span></li>
                </ul>
                <div className="flex gap-8 mb-6">
                  <div><div className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1">対応時間</div><div className="font-['Playfair_Display',serif] text-lg text-[#1a2942]">3時間</div></div>
                  <div><div className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1">月額料金</div><div className="font-['Playfair_Display',serif] text-2xl md:text-[26px] text-[#1a2942] leading-none">24,000<span className="text-base font-normal ml-0.5">円</span></div></div>
                </div>
                <a href="/services/footman/" className={TEXT_ARROW}>詳細を見る <span className="text-[#a38e65]">→</span></a>
              </div>
            </article>

            {/* Butler — 白カード */}
            <article className="bg-white border border-[#e2ddd2] flex flex-col group hover:border-[#a38e65]/70 transition-colors duration-300">
              <div className="relative h-56 sm:h-60 md:h-64 overflow-hidden">
                <img
                  src="/images/generated/jba-p-002-home-butler.jpg"
                  alt="上級執事バトラーが邸宅の書斎で家事管理とビジネスサポートを行い、主人の予定を確認する様子"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-7 sm:p-8 md:p-9 flex flex-col flex-1 text-left">
                <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm tracking-[0.06em] mb-3">ii. Butler</span>
                <h4 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl md:text-[24px] leading-[1.5] tracking-[0.02em] mb-4">上級執事による包括的サービス</h4>
                <p className="font-['Noto_Serif_JP',serif] text-[#333] text-sm sm:text-base leading-[1.95] mb-6">
                  1日8時間の利用が可能な、上級執事による包括的なパーソナルサービスです。経験豊富な執事が専門的なサポートを提供し、お客様の日常生活を快適で円滑なものに実現します。高度な家事管理からビジネスサポート、社交イベント対応まで幅広くカバーし、富裕層やVIPのお客様に最高水準のおもてなしを行います。
                </p>
                <ul className="space-y-0 mb-7">
                  <li className="flex items-start gap-3 border-b border-[#e2ddd2] py-2.5 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]"><span className="text-[#a38e65] text-[10px] mt-[7px]">◆</span><span>高度な家事管理・生活全般の統括</span></li>
                  <li className="flex items-start gap-3 border-b border-[#e2ddd2] py-2.5 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]"><span className="text-[#a38e65] text-[10px] mt-[7px]">◆</span><span>ビジネスサポート・秘書業務</span></li>
                  <li className="flex items-start gap-3 border-b border-[#e2ddd2] py-2.5 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]"><span className="text-[#a38e65] text-[10px] mt-[7px]">◆</span><span>社交イベント・接遇対応</span></li>
                </ul>
                <div className="flex gap-8 mb-6">
                  <div><div className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1">対応時間</div><div className="font-['Playfair_Display',serif] text-lg text-[#1a2942]">8時間</div></div>
                  <div><div className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1">月額料金</div><div className="font-['Playfair_Display',serif] text-2xl md:text-[26px] text-[#1a2942] leading-none">60,000<span className="text-base font-normal ml-0.5">円</span></div></div>
                </div>
                <a href="/services/butler/" className={TEXT_ARROW}>詳細を見る <span className="text-[#a38e65]">→</span></a>
              </div>
            </article>

            {/* Second Steward — 白カード */}
            <article className="bg-white border border-[#e2ddd2] flex flex-col group hover:border-[#a38e65]/70 transition-colors duration-300">
              <div className="relative h-56 sm:h-60 md:h-64 overflow-hidden">
                <img
                  src="/images/generated/jba-p-003-home-steward.jpg"
                  alt="セカンドスチュワードが大規模なパーティ会場で複数のスタッフを統括し、VIP対応を指揮する様子"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-7 sm:p-8 md:p-9 flex flex-col flex-1 text-left">
                <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm tracking-[0.06em] mb-3">iii. Second Steward</span>
                <h4 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl md:text-[24px] leading-[1.5] tracking-[0.02em] mb-4">最上級の執事サービス</h4>
                <p className="font-['Noto_Serif_JP',serif] text-[#333] text-sm sm:text-base leading-[1.95] mb-6">
                  1人の専属執事を1ヶ月20日間継続して利用できる極上のおもてなしサービスです。最高峰の執事が専任でお客様のニーズに対応し、日常生活のあらゆる面で緻密なサポートを行います。複数スタッフの統括、大規模イベント運営、VIP対応など、エグゼクティブレベルの包括的サポートを提供し、お客様の生活スタイルを深く理解した上質なサポートを実現します。
                </p>
                <ul className="space-y-0 mb-7">
                  <li className="flex items-start gap-3 border-b border-[#e2ddd2] py-2.5 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]"><span className="text-[#a38e65] text-[10px] mt-[7px]">◆</span><span>複数スタッフの統括・チーム運営</span></li>
                  <li className="flex items-start gap-3 border-b border-[#e2ddd2] py-2.5 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]"><span className="text-[#a38e65] text-[10px] mt-[7px]">◆</span><span>大規模イベントの運営・進行</span></li>
                  <li className="flex items-start gap-3 border-b border-[#e2ddd2] py-2.5 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]"><span className="text-[#a38e65] text-[10px] mt-[7px]">◆</span><span>VIP・VVIP対応の指揮</span></li>
                </ul>
                <div className="flex gap-8 mb-6">
                  <div><div className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1">対応日数</div><div className="font-['Playfair_Display',serif] text-lg text-[#1a2942]">20日間</div></div>
                  <div><div className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1">月額料金</div><div className="font-['Playfair_Display',serif] text-2xl md:text-[26px] text-[#1a2942] leading-none">680,000<span className="text-base font-normal ml-0.5">円〜</span></div></div>
                </div>
                <a href="/services/second-steward/" className={TEXT_ARROW}>詳細を見る <span className="text-[#a38e65]">→</span></a>
              </div>
            </article>
          </div>

          <div className="text-center mt-12 sm:mt-14 md:mt-16">
            <a href="/butler-services/" className={BTN08_ON_LIGHT}>執事サービスの一覧を見る</a>
          </div>
        </div>
      </section>

      {/* ===== III. 執事資格制度 — CARD-E（既採用・維持＋説明増強）===== */}
      <NavyBand no="III" ja="執事資格制度" en="Certifications" />
      <section className="py-12 sm:py-16 bg-white relative overflow-hidden">
        <div className="relative z-10 max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mb-10 sm:mb-14 text-left">
            <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-5">
              技能と知識を証明する、<span className="text-[#a38e65]">三段階の資格</span>
            </h3>
            <p className="text-base sm:text-lg text-[#333] leading-[1.95] font-['Noto_Serif_JP',serif]">
              全日本執事協会は、執事としての技能と知識を客観的に証明する三段階の資格認定制度を設けています。基礎を身につけるアソシエイトバトラーから、独立して質の高いサービスを提供できるプロフェッショナルバトラー、そして後進を指導し複雑な状況をマネジメントできるマスターバトラーまで、段階を追って専門性を高めていくことができます。いずれの資格にも執事サロンでの実地体験が含まれます。
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
            {/* Associate Butler — CARD-E */}
            <div className="bg-[#1a2942] border border-[#a38e65]/45 overflow-hidden flex flex-col group hover:border-[#a38e65] transition-colors duration-300">
              <div className="relative h-52 sm:h-60 md:h-64 overflow-hidden">
                <img
                  src="/images/generated/jba-p-004-home-associate.jpg"
                  alt="アソシエイトバトラーの研修風景。テーブルマナーの基礎を学ぶ受講生と指導する執事"
                  className="w-full h-full object-cover object-top opacity-95"
                />
                <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/80 via-black/35 to-transparent"></div>
                <span className="absolute top-4 left-5 font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-xs text-[#d7b779]">Grade I</span>
                <h3 className="absolute bottom-4 left-5 right-5 font-['Playfair_Display',serif] uppercase tracking-[0.14em] text-2xl sm:text-3xl text-[#f8f6f0]">Associate Butler</h3>
              </div>
              <div className="p-7 sm:p-8 md:p-9 flex flex-col flex-1 text-left">
                <p className="font-['Noto_Serif_JP',serif] text-[#f8f6f0] text-xl sm:text-2xl md:text-[26px] font-medium leading-[1.5] tracking-[0.02em] mb-5">執事としての基礎を身につける</p>
                <p className="font-['Noto_Serif_JP',serif] text-[#ded8cc] text-sm sm:text-base leading-[1.9] mb-6">執事としての基礎を身につけた段階の資格です。テーブルマナーやコミュニケーション技術など、基本的な対応力と心構えを評価します。</p>
                <div className="flex gap-8 border-t border-[#a38e65]/40 pt-5 mb-4">
                  <div><div className="font-['Noto_Serif_JP',serif] text-xs text-[#9d9686] tracking-[0.06em] mb-1">学習期間</div><div className="font-['Noto_Serif_JP',serif] text-lg text-[#f8f6f0]">2〜4ヶ月</div></div>
                  <div><div className="font-['Noto_Serif_JP',serif] text-xs text-[#9d9686] tracking-[0.06em] mb-1">資格料金</div><div className="font-['Noto_Serif_JP',serif] text-2xl md:text-[26px] text-[#d7b779] leading-none">48,000<span className="text-base font-normal ml-0.5">円</span></div></div>
                </div>
                <div className="font-['Noto_Serif_JP',serif] text-[13px] text-[#d7b779] mb-6">執事サロン3時間無料体験（30,000円相当）付き</div>
                <a href="/qualifications/associate-butler" className={`${BTN08_ON_NAVY} w-full mt-auto`}>詳細を見る<i className="ri-arrow-right-line ml-2"></i></a>
              </div>
            </div>

            {/* Professional Butler — CARD-E */}
            <div className="bg-[#1a2942] border border-[#a38e65]/45 overflow-hidden flex flex-col group hover:border-[#a38e65] transition-colors duration-300">
              <div className="relative h-52 sm:h-60 md:h-64 overflow-hidden">
                <img
                  src="/images/generated/jba-p-005-home-professional-w2.jpg"
                  alt="プロフェッショナルバトラーの実地訓練。独立して質の高いサービスを提供する執事の応対"
                  className="w-full h-full object-cover object-top opacity-95"
                />
                <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/80 via-black/35 to-transparent"></div>
                <span className="absolute top-4 left-5 font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-xs text-[#d7b779]">Grade II</span>
                <h3 className="absolute bottom-4 left-5 right-5 font-['Playfair_Display',serif] uppercase tracking-[0.14em] text-2xl sm:text-3xl text-[#f8f6f0]">Professional Butler</h3>
              </div>
              <div className="p-7 sm:p-8 md:p-9 flex flex-col flex-1 text-left">
                <p className="font-['Noto_Serif_JP',serif] text-[#f8f6f0] text-xl sm:text-2xl md:text-[26px] font-medium leading-[1.5] tracking-[0.02em] mb-5">幅広い知識と経験を証明</p>
                <p className="font-['Noto_Serif_JP',serif] text-[#ded8cc] text-sm sm:text-base leading-[1.9] mb-6">幅広い知識と経験を持ち、独立して質の高いサービスを提供できる段階の資格です。多様な状況に柔軟に対応できる能力を証明します。</p>
                <div className="flex gap-8 border-t border-[#a38e65]/40 pt-5 mb-4">
                  <div><div className="font-['Noto_Serif_JP',serif] text-xs text-[#9d9686] tracking-[0.06em] mb-1">学習期間</div><div className="font-['Noto_Serif_JP',serif] text-lg text-[#f8f6f0]">4〜6ヶ月</div></div>
                  <div><div className="font-['Noto_Serif_JP',serif] text-xs text-[#9d9686] tracking-[0.06em] mb-1">資格料金</div><div className="font-['Noto_Serif_JP',serif] text-2xl md:text-[26px] text-[#d7b779] leading-none">78,000<span className="text-base font-normal ml-0.5">円</span></div></div>
                </div>
                <div className="font-['Noto_Serif_JP',serif] text-[13px] text-[#d7b779] mb-6">執事サロン3時間無料体験（30,000円相当）付き</div>
                <a href="/qualifications/professional-butler" className={`${BTN08_ON_NAVY} w-full mt-auto`}>詳細を見る<i className="ri-arrow-right-line ml-2"></i></a>
              </div>
            </div>

            {/* Master Butler — CARD-E */}
            <div className="bg-[#1a2942] border border-[#a38e65]/45 overflow-hidden flex flex-col group hover:border-[#a38e65] transition-colors duration-300">
              <div className="relative h-52 sm:h-60 md:h-64 overflow-hidden">
                <img
                  src="/images/generated/jba-p-006-home-master.jpg"
                  alt="マスターバトラーの指導場面。卓越した技術で後進の執事を導く最高峰の資格保持者"
                  className="w-full h-full object-cover object-top opacity-95"
                />
                <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/80 via-black/35 to-transparent"></div>
                <span className="absolute top-4 left-5 font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-xs text-[#d7b779]">Grade III</span>
                <h3 className="absolute bottom-4 left-5 right-5 font-['Playfair_Display',serif] uppercase tracking-[0.14em] text-2xl sm:text-3xl text-[#f8f6f0]">Master Butler</h3>
              </div>
              <div className="p-7 sm:p-8 md:p-9 flex flex-col flex-1 text-left">
                <p className="font-['Noto_Serif_JP',serif] text-[#f8f6f0] text-xl sm:text-2xl md:text-[26px] font-medium leading-[1.5] tracking-[0.02em] mb-5">執事業界における最高峰</p>
                <p className="font-['Noto_Serif_JP',serif] text-[#ded8cc] text-sm sm:text-base leading-[1.9] mb-6">執事業界における最高峰の資格です。卓越した技術と知識を有し、他の執事の指導や複雑な状況のマネジメントができる能力を証明します。</p>
                <div className="flex gap-8 border-t border-[#a38e65]/40 pt-5 mb-4">
                  <div><div className="font-['Noto_Serif_JP',serif] text-xs text-[#9d9686] tracking-[0.06em] mb-1">学習期間</div><div className="font-['Noto_Serif_JP',serif] text-lg text-[#f8f6f0]">6〜8ヶ月</div></div>
                  <div><div className="font-['Noto_Serif_JP',serif] text-xs text-[#9d9686] tracking-[0.06em] mb-1">資格料金</div><div className="font-['Noto_Serif_JP',serif] text-2xl md:text-[26px] text-[#d7b779] leading-none">128,000<span className="text-base font-normal ml-0.5">円</span></div></div>
                </div>
                <div className="font-['Noto_Serif_JP',serif] text-[13px] text-[#d7b779] mb-6">執事サロン3時間無料体験（30,000円相当）付き</div>
                <a href="/qualifications/master-butler" className={`${BTN08_ON_NAVY} w-full mt-auto`}>詳細を見る<i className="ri-arrow-right-line ml-2"></i></a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== IV. 専門資格制度 — 全幅エディトリアル行型（左詰め解消・価格/矢印の縦ライン整列）===== */}
      <NavyBand no="IV" ja="専門資格制度" en="Specialist Certifications" />
      <section className="py-12 sm:py-16 bg-white relative overflow-hidden">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mb-10 sm:mb-12 text-left">
            <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-5">
              特定分野に特化した、<span className="text-[#a38e65]">専門知識資格</span>
            </h3>
            <p className="text-base sm:text-lg text-[#333] leading-[1.95] font-['Noto_Serif_JP',serif]">
              執事の技能は幅広い専門分野に及びます。食事サービスや旅行手配、ビジネスマナーといった実務分野から、アフタヌーンティーや紅茶、装いの美学まで、それぞれの分野に特化した知識資格をご用意しています。基本資格と組み合わせることで、お客様に提供できるサービスの幅と深さを大きく広げることができます。
            </p>
          </div>

          {/* 全幅エディトリアル行型（1行1資格・列整列） */}
          <div className="border-t-2 border-[#1a2942]">
            {[
              { ja: '食事サービスの専門家', en: 'Dining Coordinator', desc: 'テーブルセッティングから料理の提供、食事マナーの指導まで、ダイニング全般のコーディネートを担当。', fee: '58,000円', href: '/qualifications/dining-coordinator' },
              { ja: '旅行計画のスペシャリスト', en: 'Travel Concierge', desc: '交通手段の手配、宿泊施設の選定、観光プランの提案など、旅行計画から現地サポートまでを担当。', fee: '58,000円', href: '/qualifications/travel-concierge' },
              { ja: 'ビジネスマナーの専門家', en: 'Business Manner', desc: '会議の進行、来客対応、ビジネス文書の作成など、企業環境でのマナーとプロトコルに特化。', fee: '58,000円', href: '/qualifications/business-manner' },
              { ja: 'アフタヌーン・ティー', en: 'Afternoon Tea', desc: 'アフタヌーンティーの文化とマナー。英国式の正統なサービスを習得する知識資格。', fee: '38,000円', href: '/qualifications/afternoon-tea' },
              { ja: 'ティー・ソムリエ', en: 'Tea Sommelier', desc: '紅茶の専門知識。茶葉の選び方から淹れ方まで、紅茶を極める知識資格。', fee: '38,000円', href: '/qualifications/tea-sommelier' },
              { ja: 'バトラー・スタイル＆ユニフォーム', en: 'Butler Style & Uniform', desc: '執事の装いと美学。フォーマルウェアの知識と品格ある装いを学ぶ知識資格。', fee: '38,000円', href: '/qualifications/butler-style-uniform' },
            ].map((q) => (
              <a
                key={q.href}
                href={q.href}
                className="group flex flex-col md:flex-row md:items-center gap-3 md:gap-10 py-6 sm:py-7 border-b border-[#e2ddd2] hover:bg-[#f8f6f0]/60 transition-colors duration-300"
              >
                {/* 資格名（英字イタリック金 + 和文タイトル大） */}
                <div className="md:w-[30%] md:shrink-0">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1.5">{q.en}</span>
                  <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.45] tracking-[0.02em] group-hover:text-[#8f7a56] transition-colors">{q.ja}</h4>
                </div>
                {/* 説明（1〜2行・伸縮） */}
                <p className="flex-1 font-['Noto_Serif_JP',serif] text-sm sm:text-[15px] text-[#4a4638] leading-[1.85]">{q.desc}</p>
                {/* 価格（右揃え・serif）＋矢印（右端）縦ライン整列 */}
                <div className="flex items-center justify-between md:justify-end gap-6 md:w-[150px] md:shrink-0 border-t border-[#e2ddd2] md:border-0 pt-3 md:pt-0">
                  <Fee value={q.fee} className="font-['Playfair_Display',serif] text-[#1a2942] text-2xl md:w-[100px] md:text-right leading-none" unitClassName="text-base font-normal ml-0.5" />
                  <span className="text-[#a38e65] text-lg w-5 text-right group-hover:translate-x-1 transition-transform">→</span>
                </div>
              </a>
            ))}
          </div>

          <div className="text-center mt-12 sm:mt-14">
            <a href="/qualifications" className={BTN08_ON_LIGHT}>専門資格一覧を見る</a>
          </div>
        </div>
      </section>

      {/* ===== V. セミナー・イベント — 分割エディトリアル（画像半分＋テキスト半分／SEC-N02 nsec-split 準拠）===== */}
      <NavyBand no="V" ja="セミナー・イベント" en="Seminars & Events" />
      <section className="py-12 sm:py-16 bg-white relative overflow-hidden">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 items-stretch border border-[#e2ddd2] overflow-hidden">
            {/* 左: 画像フル */}
            <div className="relative min-h-[320px] lg:min-h-[520px] bg-black order-1">
              <img
                src="/images/generated/jba-p-007-home-seminar-v2.jpg"
                alt="執事が受講者にチームを効果的に導くリーダーシップの技術を指導する法人向けセミナーの様子"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            {/* 右: テキスト列（リード厚＋3つの研修を小見出しで） */}
            <div className="order-2 flex flex-col justify-center px-8 sm:px-12 lg:px-14 py-12 sm:py-14 lg:py-16 text-left">
              <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm tracking-[0.06em] mb-4">Seminars & Events</span>
              <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-5">
                執事に学ぶ、<span className="text-[#a38e65]">実践の学び</span>
              </h3>
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8">
                執事が現場で磨いてきたおもてなしの技術は、ビジネスの現場でも力を発揮します。全日本執事協会では、企業や個人を対象に、リーダーシップ・ホスピタリティ・コミュニケーションといったテーマの実践的なセミナーを開催しています。一日三時間の集中プログラムで、明日から使える所作と考え方を身につけていただけます。
              </p>
              <div className="border-t border-[#e2ddd2]">
                <div className="py-4 border-b border-[#e2ddd2]">
                  <h4 className="jba-display font-medium text-[#1a2942] text-lg leading-[1.5] mb-1">執事に学ぶリーダーシップの技術</h4>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.8]">「見えないリーダーシップ」を学び、チームを効果的に導く技術を習得します。</p>
                </div>
                <div className="py-4 border-b border-[#e2ddd2]">
                  <h4 className="jba-display font-medium text-[#1a2942] text-lg leading-[1.5] mb-1">執事に学ぶ心からのおもてなし</h4>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.8]">最高峰のおもてなし精神を学び、ゲストの期待を超えるサービスを提供する技術を習得します。</p>
                </div>
                <div className="py-4 border-b border-[#e2ddd2]">
                  <h4 className="jba-display font-medium text-[#1a2942] text-lg leading-[1.5] mb-1">執事に学ぶ伝える力を磨く技術</h4>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.8]">傾聴力・表現力・非言語コミュニケーションを学び、円滑な人間関係の構築を実現します。</p>
                </div>
              </div>
              <div className="mt-8">
                <a href="/seminars" className={BTN08_ON_LIGHT}>セミナー・イベント一覧を見る</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== VI. ニュース・お知らせ — リスト行（thebutler型 NEWS: 日付＋種別チップ＋タイトル＋詳しく→。カード0）===== */}
      <NavyBand no="VI" ja="ニュース・お知らせ" en="News & Updates" />
      <section className="py-12 sm:py-16 bg-white relative overflow-hidden">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mb-10 sm:mb-12 text-left">
            <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-5">
              協会の<span className="text-[#a38e65]">最新情報</span>と活動報告
            </h3>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.95]">
              執事業界の動向や国内外の最新ニュース、協会のイベントや活動の記録をお届けします。世界のラグジュアリーホテルにおけるバトラーサービスの潮流から、テクノロジーとおもてなしの融合まで、執事文化の「いま」をご覧いただけます。
            </p>
          </div>

          {/* NEWS リスト行 */}
          <div className="border-t border-[#e2ddd2]">
            {[
              { date: '2025.10.15', cat: '重要', title: 'Raffles Sentosa 高級エスケープパッケージ（1泊4,985SGDから）', href: '/news/raffles-sentosa-butler-package' },
              { date: '2025.09.17', cat: '業界動向', title: 'Raffles London「The Butler Did It」キャンペーン第2章', href: '/news/raffles-london-campaign' },
              { date: '2025.09.12', cat: 'イベント', title: 'Downton Abbey: The Grand Finale 映画公開', href: '/news/downton-abbey-grand-finale' },
              { date: '2025.08.14', cat: 'テクノロジー', title: 'AIホスピタリティ革命', href: '/news/ai-hospitality-revolution' },
              { date: '2025.08.01', cat: '報告', title: 'グラント・ハロルド王室バトラー回顧録出版', href: '/news/grant-harrold-royal-butler-memoir' },
              { date: '2024.09.10', cat: '業界動向', title: 'Raffles Hotels & Resorts グローバルブランドキャンペーン「The Butler Did It」', href: '/news/raffles-butler-campaign' },
            ].map((n) => (
              <a
                key={n.href}
                href={n.href}
                className="group flex items-center gap-4 sm:gap-6 py-5 border-b border-[#e2ddd2] hover:bg-[#f8f6f0]/60 transition-colors duration-300"
              >
                <span className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm shrink-0 w-[76px] sm:w-[92px]">{n.date}</span>
                <span className="shrink-0 whitespace-nowrap border border-[#a38e65]/60 text-[#8f7a56] text-[11px] tracking-[0.04em] py-1 w-[104px] text-center">{n.cat}</span>
                <h4 className="flex-1 min-w-0 jba-display font-normal text-[#1a2942] text-[15px] sm:text-lg leading-[1.6] tracking-[0.01em] group-hover:text-[#8f7a56] transition-colors truncate sm:whitespace-normal">{n.title}</h4>
                <span className="hidden sm:inline-flex items-center gap-1.5 shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56]">詳しく <span className="text-[#a38e65] group-hover:translate-x-1 transition-transform inline-block">→</span></span>
              </a>
            ))}
          </div>

          <div className="text-center mt-12 sm:mt-14">
            <a href="/news" className={BTN08_ON_LIGHT}>ニュース・お知らせ一覧を見る</a>
          </div>
        </div>
      </section>

      {/* ===== VII. 会員募集 — リード厚＋細罫テーブル調（会員種別比較を罫線テーブルで。カード群を廃止）===== */}
      <NavyBand no="VII" ja="会員募集" en="Membership" />
      <section className="py-12 sm:py-16 bg-white relative overflow-hidden">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-12 sm:mb-16">
            <div className="order-2 lg:order-1 text-left">
              <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-5">
                執事文化の未来を、<span className="text-[#a38e65]">一緒に築きませんか</span>
              </h3>
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.95] mb-4">
                当協会では、執事文化の普及と発展に賛同いただける個人・法人会員を募集しています。会員の皆様には、資格取得支援、セミナー優待、ネットワーキングの機会など、様々な特典をご用意しています。
              </p>
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.95]">
                プロフェッショナルとしてのスキル向上と、執事文化の継承に貢献できる環境を提供いたします。準会員・正会員・法人会員の三つの区分から、目的に合わせてお選びいただけます。
              </p>
            </div>
            <div className="order-1 lg:order-2">
              <img src="/images/generated/jba-p-008-home-gathering-w2.jpg" alt="会員が集う懇親会で執事文化の発展について語り合う様子" className="w-full h-[300px] sm:h-[400px] md:h-[460px] object-cover border border-[#a38e65]/30" />
            </div>
          </div>

          {/* 細罫テーブル調（会員種別比較） */}
          <div className="border-t-2 border-[#1a2942] overflow-x-auto">
            <table className="w-full border-collapse min-w-[640px]">
              <thead>
                <tr className="border-b border-[#e2ddd2]">
                  <th className="text-left py-5 pr-4 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.08em] font-medium align-bottom w-[26%]">会員区分</th>
                  <th className="text-left py-5 px-4 align-bottom">
                    <div className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl">準会員</div>
                    <div className="font-['Playfair_Display',serif] text-[#a38e65] text-2xl sm:text-[26px] mt-1">¥30,000<span className="text-xs text-[#8f7a56] ml-1">/年</span></div>
                  </th>
                  <th className="text-left py-5 px-4 align-bottom bg-[#f8f6f0]">
                    <div className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl">正会員</div>
                    <div className="font-['Playfair_Display',serif] text-[#a38e65] text-2xl sm:text-[26px] mt-1">¥60,000<span className="text-xs text-[#8f7a56] ml-1">/年</span></div>
                  </th>
                  <th className="text-left py-5 px-4 align-bottom">
                    <div className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl">法人会員</div>
                    <div className="font-['Playfair_Display',serif] text-[#a38e65] text-2xl sm:text-[26px] mt-1">¥150,000<span className="text-xs text-[#8f7a56] ml-1">/年</span></div>
                  </th>
                </tr>
              </thead>
              <tbody className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]">
                <tr className="border-b border-[#e2ddd2]">
                  <td className="py-4 pr-4 text-[#1a2942] font-medium">会員証</td>
                  <td className="py-4 px-4">デジタル会員証</td>
                  <td className="py-4 px-4 bg-[#f8f6f0]">公式会員証</td>
                  <td className="py-4 px-4">登録社員5名まで正会員特典</td>
                </tr>
                <tr className="border-b border-[#e2ddd2]">
                  <td className="py-4 pr-4 text-[#1a2942] font-medium">セミナー割引</td>
                  <td className="py-4 px-4">10%割引</td>
                  <td className="py-4 px-4 bg-[#f8f6f0]">20%割引</td>
                  <td className="py-4 px-4">法人向け特別セミナー案内</td>
                </tr>
                <tr className="border-b border-[#e2ddd2]">
                  <td className="py-4 pr-4 text-[#1a2942] font-medium">資格試験割引</td>
                  <td className="py-4 px-4">受験料10%割引</td>
                  <td className="py-4 px-4 bg-[#f8f6f0]">受験料15%割引</td>
                  <td className="py-4 px-4">企業向けカスタム研修の相談</td>
                </tr>
                <tr className="border-b border-[#e2ddd2]">
                  <td className="py-4 pr-4 text-[#1a2942] font-medium">その他特典</td>
                  <td className="py-4 px-4">月次メールマガジン／会員専用コンテンツ</td>
                  <td className="py-4 px-4 bg-[#f8f6f0]">特別セミナー優先予約／オンライン勉強会</td>
                  <td className="py-4 px-4">採用情報の優先掲載</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* 締めCTAパネル */}
          <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 md:p-14 text-center mt-12 sm:mt-14">
            <h3 className="jba-display font-medium text-2xl sm:text-3xl text-[#f8f6f0] tracking-[0.04em]"><span className="inline-block">今すぐ会員登録して</span><span className="inline-block">執事文化の一員になりませんか</span></h3>
            <div className="w-11 h-px bg-[#a38e65] mx-auto mt-4 mb-6"></div>
            <p className="text-base sm:text-lg text-[#e7e2d8] mb-8 font-['Noto_Serif_JP',serif] max-w-2xl mx-auto leading-[1.9]"><span className="inline-block">お申し込みは簡単です。</span><span className="inline-block">オンラインフォームからお手続きいただけます。</span><span className="inline-block">ご不明な点がございましたら、</span><span className="inline-block">お気軽にお問い合わせください。</span></p>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
              <a href="/membership" className={BTN08_ON_NAVY}>会員登録はこちら</a>
              <a href="/contact" className={BTN08_ON_NAVY}>お問い合わせ</a>
            </div>
          </div>
        </div>
      </section>

      </Layout>
    </>
  );
}
